import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Flex, Skeleton, Typography } from 'antd';

import { PLAN, RETURNABILITY } from '@app/constants';
import type { NAnalytics } from '@app/types/Analytics';
import { divideAmount } from '@app/utils';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

const { Title, Text } = Typography;
const PREVIOUS_MONTH_INFO_TEXT = [
  'За последние 3 месяца',
  'За последние 2 месяца',
  'За последний месяц',
];

export const Widget: NAnalytics.Widget = ({
  isLoading,
  data,
  infoText,
  title,
  mainValueSign,
  dataWidgetId,
}) => {
  const truncateNumberString = (inputString: string) => {
    if (inputString.includes('.')) {
      const [integerPart, decimalPart] = inputString.split('.');

      if (decimalPart.length > 2) {
        const truncatedDecimalPart = decimalPart.slice(0, 2);

        return `${integerPart}.${truncatedDecimalPart}`.trim();
      }
    }

    return inputString;
  };

  const deviationValue = +(data?.Deviation || 0);

  const isPlanWidget = data?.WidgetId === PLAN;
  const isOtherWidget = data?.WidgetId !== PLAN;

  const isGeneralPositiveDeviation = deviationValue > 0;
  const isGeneralNegativeDeviation = deviationValue < 0;
  const isPlanPositiveDeviation = deviationValue >= 100;
  const isPlanNegativeDeviation = deviationValue < 100;

  const blackColor = 'rgba(0, 0, 0, 0.88)';
  const redColor = 'rgb(225, 79, 79)';
  const greenColor = '#18936E';

  let deviationColor = blackColor;

  if (
    (isOtherWidget && isGeneralNegativeDeviation) ||
    (isPlanWidget && isPlanNegativeDeviation && deviationValue !== 0)
  ) {
    deviationColor = redColor;
  }

  if (
    (isOtherWidget && isGeneralPositiveDeviation) ||
    (isPlanWidget && isPlanPositiveDeviation)
  ) {
    deviationColor = greenColor;
  }

  return (
    <div className="widget" data-widget-id={dataWidgetId}>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <Card title={`${title}`}>
          <Title
            level={4}
            style={{
              margin: '0',
              color:
                Number(data?.Value) < 0
                  ? 'rgb(225, 79, 79)'
                  : 'rgba(0, 0, 0, 0.88)',
            }}
          >
            {data?.Value && divideAmount(data?.Value)}{' '}
            {mainValueSign && mainValueSign}
          </Title>
          <br />
          {data?.WidgetId === RETURNABILITY ? (
            <Flex vertical>
              {PREVIOUS_MONTH_INFO_TEXT.map((text, index) => (
                <div
                  key={`${dataWidgetId}-${text}-${index}`}
                  className="widget-info-container"
                >
                  <Text style={{ maxWidth: '150px' }}>{text}</Text>
                  <Text
                    style={{
                      color: deviationColor,
                    }}
                    strong
                  >
                    {data?.Deviations?.[index] &&
                      truncateNumberString(
                        String(divideAmount(data?.Deviations?.[index]))
                      )}
                    {data?.Deviations?.[index] && <b>%</b>}
                  </Text>
                </div>
              ))}
            </Flex>
          ) : (
            <div className="widget-info-container">
              <Text style={{ maxWidth: '150px' }}>{infoText}</Text>
              <Text
                style={{
                  color: deviationColor,
                }}
                strong
              >
                {data?.Deviation &&
                  truncateNumberString(String(divideAmount(data?.Deviation)))}
                {data?.Deviation && <b>%</b>}
                {data?.WidgetId !== PLAN && Number(data?.Deviation) > 0 ? (
                  <FontAwesomeIcon
                    icon={'fa-solid fa-turn-up' as IconProp}
                    size="sm"
                    style={{ width: '30px', marginRight: '5px' }}
                  />
                ) : null}
                {data?.WidgetId !== PLAN && Number(data?.Deviation) < 0 ? (
                  <FontAwesomeIcon
                    icon={'fa-solid fa-turn-down' as IconProp}
                    size="sm"
                    style={{ width: '30px', marginRight: '5px' }}
                  />
                ) : null}
              </Text>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
